import React, { Component } from "react";
import { toast } from "react-toastify";
import { getRoomAvailability, getBranchList } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Chip } from "../../components/Console/ConsoleUI";
import { isBranchScopedAdmin, getUserBranchName, getSelectedBranchId } from "../../services/PermissionService";

/**
 * Availability — what the app can still sell, per room type per night, at one
 * branch. Read-only: the numbers come from room stock, out-of-service blocks
 * and bookings (GET /api/rooms/availability), counted in the branch's own
 * calendar; a "night" is the date the guest sleeps there.
 *
 * A type marked "old room count" has no numbered rooms at this branch yet, so
 * its total is the type's legacy noOfRooms — the hint links to room stock.
 */

const CSS = `
.av-grid{border-collapse:separate;border-spacing:0;font-size:13px;min-width:100%}
.av-grid th,.av-grid td{padding:0;text-align:center;border-bottom:1px solid ${C.panel}}
.av-grid th{font-weight:400;color:${C.taupe};padding:8px 4px;white-space:nowrap}
.av-grid th.av-type,.av-grid td.av-type{text-align:left;position:sticky;left:0;background:#fff;
  padding:10px 14px 10px 4px;min-width:180px;z-index:1}
.av-cell{margin:4px 2px;border-radius:9px;padding:7px 0;min-width:46px;line-height:1.15}
.av-weekend{background:${C.surface}}
`;

const DAY_OPTIONS = [7, 14, 31, 62];

/** YYYY-MM-DD plus whole days — calendar arithmetic, no timezone involved. */
const shiftDay = (ymd, n) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const utcDate = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/** Sold out red, last rooms amber, otherwise calm; out-of-service shown inside. */
const cellTone = (n) => {
  if (n.total === 0) return { bg: C.panel, fg: C.taupeLight };
  if (n.available <= 0) return { bg: "rgba(192,32,43,.12)", fg: C.red };
  if (n.available <= Math.max(1, Math.round(n.total * 0.2))) return { bg: "rgba(224,165,63,.18)", fg: "#8a6115" };
  return { bg: "rgba(76,175,80,.12)", fg: "#2f7d33" };
};

class RoomAvailability extends Component {
  constructor(props) {
    super(props);
    this.state = {
      branches: [],
      branchId: isBranchScopedAdmin() ? null : getSelectedBranchId() || "",
      from: null, // null = let the server pick "today" in the branch's timezone
      today: null,
      days: 14,
      timezone: null,
      rows: [],
      isLoaded: false,
    };
  }

  componentDidMount() {
    if (isBranchScopedAdmin()) {
      this.load();
      return;
    }
    getBranchList().then((res) => {
      const branches = res && res.success && Array.isArray(res.data) ? res.data : [];
      const keep = branches.some((b) => String(b._id) === String(this.state.branchId));
      this.setState({ branches, branchId: keep ? this.state.branchId : branches[0] ? String(branches[0]._id) : "" }, this.load);
    });
  }

  load = () => {
    const { branchId, from, days } = this.state;
    if (!isBranchScopedAdmin() && !branchId) {
      this.setState({ isLoaded: true, rows: [] });
      return;
    }
    this.setState({ isLoaded: false });
    const params = { days, ...(branchId ? { branchId } : {}), ...(from ? { from } : {}) };
    getRoomAvailability(params).then((res) => {
      if (res && res.success) {
        this.setState((s) => ({
          isLoaded: true,
          rows: res.data || [],
          from: res.from,
          timezone: res.timezone,
          today: s.today || (from ? s.today : res.from),
        }));
      } else {
        this.setState({ isLoaded: true, rows: [] });
        toast.error((res && res.message) || "Could not load availability");
      }
    });
  };

  go = (patch) => this.setState(patch, this.load);

  render() {
    const { branches, branchId, from, today, days, timezone, rows, isLoaded } = this.state;
    const scoped = isBranchScopedAdmin();
    const nights = rows.length && rows[0].nights ? rows[0].nights.map((n) => n.date) : [];
    const legacy = rows.filter((r) => r.source === "room_type");
    const soldOut = rows.reduce((s, r) => s + r.nights.filter((n) => n.total > 0 && n.available <= 0).length, 0);

    return (
      <ConsolePage
        title="Availability"
        subtitle={`${scoped ? getUserBranchName() || "Your branch" : "Rooms the app can still sell"}${timezone ? ` · ${timezone.replace("_", " ")} nights` : ""}`}
        actions={
          <React.Fragment>
            {!scoped && (
              <select
                aria-label="Branch"
                value={branchId}
                onChange={(e) => this.go({ branchId: e.target.value })}
                style={{ height: 42, borderRadius: 21, border: `1px solid ${C.border}`, background: "#fff", padding: "0 14px", fontSize: 14, color: C.brownDeep, fontFamily: "inherit" }}
              >
                {branches.map((b) => <option key={b._id} value={b._id}>{b.name || b.code}</option>)}
              </select>
            )}
            <Btn kind="quiet" icon="door_front" onClick={() => this.props.history.push("/rooms/units")}>Room stock</Btn>
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Btn kind="quiet" icon="chevron_left" disabled={!from} onClick={() => this.go({ from: shiftDay(from, -days) })}>Earlier</Btn>
            <input
              type="date"
              aria-label="First night"
              value={from || ""}
              onChange={(e) => e.target.value && this.go({ from: e.target.value })}
              style={{ height: 42, borderRadius: 12, border: `1px solid ${C.border}`, padding: "0 12px", fontSize: 14, color: C.ink, fontFamily: "inherit" }}
            />
            <Btn kind="quiet" icon="chevron_right" disabled={!from} onClick={() => this.go({ from: shiftDay(from, days) })}>Later</Btn>
            {today && from !== today && <Btn kind="ghost" onClick={() => this.go({ from: today })}>Tonight</Btn>}
            <select
              aria-label="Nights shown"
              value={days}
              onChange={(e) => this.go({ days: Number(e.target.value) })}
              style={{ height: 42, borderRadius: 21, border: `1px solid ${C.border}`, background: "#fff", padding: "0 14px", fontSize: 14, color: C.brownDeep, fontFamily: "inherit" }}
            >
              {DAY_OPTIONS.map((d) => <option key={d} value={d}>{d} nights</option>)}
            </select>
            {isLoaded && soldOut > 0 && <Chip tone="bad">{soldOut} sold-out night{soldOut === 1 ? "" : "s"}</Chip>}
          </div>

          {legacy.length > 0 && (
            <div style={{ padding: "12px 16px", borderRadius: 14, background: "#fbf0dd", fontSize: 13, color: C.taupe }}>
              {legacy.map((r) => r.type).join(", ")} {legacy.length === 1 ? "has" : "have"} no numbered rooms at this branch, so the app
              sells {legacy.length === 1 ? "its" : "their"} old room count.{" "}
              <a href="/rooms/units" onClick={(e) => { e.preventDefault(); this.props.history.push("/rooms/units"); }} style={{ color: C.brown, fontWeight: 500 }}>
                Add rooms to control availability
              </a>
            </div>
          )}

          <Panel title="Rooms left per night" subtitle="Available of total. Hover a night for booked and out-of-service rooms.">
            <div style={{ marginTop: 14, overflowX: "auto" }}>
              {!isLoaded ? (
                <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>Loading…</div>
              ) : !rows.length ? (
                <div style={{ padding: "40px 0", textAlign: "center", color: C.taupeLight }}>No room types are sold at this branch.</div>
              ) : (
                <table className="av-grid">
                  <thead>
                    <tr>
                      <th className="av-type">Room type</th>
                      {nights.map((d) => {
                        const dt = utcDate(d);
                        const wd = dt.getUTCDay();
                        return (
                          <th key={d} className={wd === 0 || wd === 6 ? "av-weekend" : ""}>
                            <div>{dt.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" })}</div>
                            <div style={{ color: C.ink, fontWeight: d === today ? 600 : 400 }}>
                              {dt.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" })}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.roomTypeId}>
                        <td className="av-type">
                          <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>{r.type}</div>
                          {r.source === "room_type" && <div style={{ marginTop: 2, fontSize: 12, color: "#8a6115" }}>Old room count</div>}
                        </td>
                        {r.nights.map((n) => {
                          const tone = cellTone(n);
                          return (
                            <td key={n.date} className={[0, 6].includes(utcDate(n.date).getUTCDay()) ? "av-weekend" : ""}>
                              <div
                                className="av-cell"
                                style={{ background: tone.bg, color: tone.fg }}
                                title={`${n.date}: ${n.available} of ${n.total} free · ${n.booked} booked${n.outOfService ? ` · ${n.outOfService} out of service` : ""}`}
                              >
                                <div style={{ fontSize: 15, fontWeight: 600 }}>{n.available}</div>
                                <div style={{ fontSize: 11, opacity: 0.8 }}>
                                  of {n.total}{n.outOfService ? ` · ${n.outOfService}⚒` : ""}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default RoomAvailability;
